/**
 * Full-size view of one branch.
 *
 * Opened mid-session, so it closes on Escape and on backdrop click, and it
 * restores focus on the way out — a GM who opened this with the keyboard
 * should not lose their place in the storyboard.
 */
import { useEffect, useRef } from 'react';

import type { Branch, Frame } from '@/api/types';

interface Props {
  branch: Branch | undefined;
  frames: Frame[];
  onClose: () => void;
}

export function Lightbox({ branch, frames, onClose }: Props) {
  const dialog = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    dialog.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (restoreTo.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  if (!branch) return null;

  return (
    <div className="lightbox" onClick={onClose}>
      <div
        className="lightbox__panel"
        role="dialog"
        aria-modal="true"
        aria-label={branch.label}
        tabIndex={-1}
        ref={dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lightbox__head">
          <div>
            <p className="branch__gate">{branch.label}</p>
            <h2 className="lightbox__title">{branch.narrative}</h2>
          </div>
          <button type="button" className="button button--quiet" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="lightbox__frames">
          {frames.map((frame) =>
            frame.url ? (
              <figure key={frame.id} className="lightbox__figure">
                <img src={frame.url} alt={frame.label ?? branch.label} />
                {frame.label ? <figcaption>{frame.label}</figcaption> : null}
              </figure>
            ) : (
              <div key={frame.id} className="lightbox__pending">
                <div className="lightbox__pending-head">
                  <strong>{frame.label ?? 'Frame'}</strong>
                  <span
                    className={
                      frame.status === 'failed'
                        ? 'badge badge--warn'
                        : 'badge lightbox__rendering'
                    }
                  >
                    {pendingStatus(frame)}
                  </span>
                </div>
                <p>{frame.prompt}</p>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function pendingStatus(frame: Frame): string {
  switch (frame.status) {
    case 'queued':
      return 'Queued to render';
    case 'rendering':
      return 'Rendering';
    case 'detecting':
      return 'Finishing image';
    case 'failed':
      return 'Render failed';
    default:
      return 'Rendering';
  }
}
